/**
 * **The recovery ladder — what a resident gets instead of being permanently locked out.**
 *
 * A production iOS resident was signed in, was silently returned to the OTP screen, and then could
 * not sign back in: the code verified server-side and the client rendered `storageUnavailable`.
 * The device-local IndexedDB for that origin was in a structurally bad state, established **by
 * elimination** — 50 GB free, every other iOS user fine, the same web bundle working from a
 * reinstall of the same build against the same account. Replacing the app container is what fixed
 * it, and a resident cannot do that from inside the app.
 *
 * **The product defect is independent of the root cause, and was fixed without knowing it.** A
 * local store that goes bad locks a resident out for good, with no in-app recovery and an error
 * string telling them to try a different browser — advice that is unreachable inside the App Store
 * container, which renders this exact bundle. The ladder below is that fix.
 *
 * **D309 then named the cause from a recurrence, and it is NOT a damaged database.** iOS killed the
 * WKWebView's Networking process — the process that serves IndexedDB — while the app sat
 * backgrounded under memory pressure; the resident's database was never damaged, and what the
 * reinstall replaced was the *process*, not the container. That has two consequences for everything
 * below. **The ladder cannot recover that mode** — both rungs reopen through the same dead
 * `IDBConnectionProxy` — and, worse, **it was destroying a healthy database when it tried**, which
 * {@link isStructuralStoreFailure}'s second exclusion is what stops. {@link
 * recoverLostStoreConnection} is the rung that does reach it, and it is a page reload rather than a
 * rung on this ladder.
 *
 * ---
 *
 * ## THE ORDERING IS LOAD-BEARING, AND IT IS THE WHOLE SAFETY ARGUMENT
 *
 * `destroy()` takes the `outbox` with it, and `outbox` is the one store `device-cache/store.ts`
 * says must never be discarded: a receipt is a record that a gate **already physically opened**
 * while the phone had no reception — the only authoritative state the client holds that the server
 * has never seen. `DERIVED_STORES` excludes it deliberately. So auto-destroy is only acceptable
 * because the salvage makes it non-destructive.
 *
 * **The credential write happens BEFORE the replay, so the sign-in is committed before any receipt
 * is touched.** That makes *"the salvage broke the sign-in"* impossible **by construction** rather
 * than by a fallback path.
 *
 * **A rung between 1 and 2 that retried the destroy *without* salvage was considered and refused
 * on that ground.** It would only ever be reachable when the salvage itself had a bug — a
 * mechanism that fires only on our own mistake and cannot be meaningfully tested. Do not
 * reintroduce it. If you find yourself wanting it, that is the signal that the replay has crept
 * above the write; fix the ordering instead.
 *
 * ## A TIMEOUT MUST NEVER REACH THIS MODULE
 *
 * Before this ladder existed, a false transaction timeout cost a rejection the caller already
 * handled. **After it, a false timeout would destroy a healthy database.** That is why
 * {@link STORE_TRANSACTION_TIMEOUT_MS} being narrowed to 2 s and this ladder are one change and
 * not two: narrowing alone raises the rate at which good data is destroyed.
 * {@link isStructuralStoreFailure} is the interlock, and `storeRecovery.test.ts` demonstrates it
 * red rather than asserting only on the rendered string — a copy-only assertion cannot see
 * `destroy()` firing anyway, which is precisely the failure this guards.
 */

import {
  type KeyValueStore,
  STORE_SALVAGE_TIMEOUT_MS,
  StoreConnectionLostError,
  StoreTransactionTimeoutError,
  errorNameOf,
  openDeviceStore,
  reopenDeviceStore,
} from '../device-cache/store'
import { claimStoreReload } from '../pwa/recoveryMarker'
import { type RecoveryRung, reportCredentialStoreFailure, reportStoreConnectionLost } from '../telemetry/sink'
import { TIMED_OUT, deadline } from './timeout'

/**
 * The four `DOMException` names that mean *the database is structurally wrong*, and the only
 * failures allowed to destroy it.
 *
 * **Everything else is reported and not acted on**, which is the conservative direction and also
 * the one that produces knowledge: an unrecognised name still reaches the sink with
 * `rung: 'none'`, so the real distribution of damage modes arrives without anyone having guessed
 * at it first. Widen this list from that data, never from reasoning.
 */
const STRUCTURAL_FAILURES: readonly string[] = [
  'QuotaExceededError',
  'UnknownError',
  'NotFoundError',
  'InvalidStateError',
]

/**
 * **The interlock — now two exclusions, and the second is the one that was destroying healthy
 * databases on the most likely iOS failure.**
 *
 * `false` for a timeout, explicitly and first, because that is the one this was written to exclude.
 * `false` for a **lost connection**: `InvalidStateError` is on the structural list above and a dead
 * `IDBConnectionProxy` throws exactly that, so rung 1 was calling `destroy()` on a database with
 * nothing wrong with it — and the salvage that is supposed to make auto-destroy safe reads through
 * the same dead connection, recovers nothing, and loses the `outbox` receipts with it. `false` for
 * anything whose name is not one of the four above.
 *
 * **Both exclusions are ordered before the list rather than subtracted from it**, because the list
 * is a set of `DOMException` *names* and neither of these is distinguishable by name. Removing
 * `InvalidStateError` from the list instead would have been the reflexive fix and is wrong: a handle
 * we closed ourselves throws it too, and that one *is* structural.
 */
export function isStructuralStoreFailure(error: unknown): boolean {
  if (error instanceof StoreTransactionTimeoutError) return false
  if (error instanceof StoreConnectionLostError) return false
  return STRUCTURAL_FAILURES.includes(errorNameOf(error))
}

/**
 * **The resume rung — reload the page instead of signing the resident out.**
 *
 * Returns `true` when this document has started a reload and the caller must stop: the document is
 * going away, and anything it does after this point runs in a page that is being replaced.
 *
 * ## WHY A RELOAD IS THE ONLY THING THAT WORKS HERE
 *
 * The rungs above cannot reach this failure on iOS below 26.5. Both reopen through the same dead
 * `IDBConnectionProxy` (WebKit 309386), so a *new* `indexedDB.open()` fails identically until the
 * page is reloaded. They would report `errorName: "InvalidStateError"`, `rung: "none"`, and that is
 * the whole of what they can do. A reload is not one more rung on the same ladder; it is the only
 * act that replaces the proxy.
 *
 * ## WHY IT GOES HERE AND NOT AT THE LADDER, WHICH IS THE WHOLE VALUE
 *
 * **At this moment the credentials are still on disk and still correct.** The refresh succeeded;
 * only the write of the rotated token failed. So the reload costs a splash screen and **no D65/D88
 * cap slot** — the resident never sees the OTP screen. Once `endSession('storage-lost')` has run,
 * the same reload costs a fresh SMS and one of five slots per rolling 5 h, which is the objection
 * `storeRecovery`'s rung 2 records and it still holds **at that position**. The two send slots the
 * incident's resident actually spent recovering by hand are what priced this.
 *
 * ## D137 — THE APP NEVER RELOADS ITSELF, AND THIS IS THE ONE EXCEPTION, MINTED
 *
 * D137 refuses an unprompted reload because an app that reloads itself can do it inside D134's
 * confirm window, at the gate. **That risk is real here and was taken knowingly** (D309): a resume
 * is not a quiet moment — D140 restores suspended confirm polling and D275 records that a resident
 * can background the app during a ~1–2 s BLE exchange — so this reload *can* land mid-command. The
 * gate is unaffected either way; what is lost is the app's report of an open the resident asked
 * for. The alternative, declining while a command is live, was weighed and refused: it spends an
 * SMS and one of five cap slots on exactly the resident who is most likely to be affected, to
 * protect a sentence rather than a gate. **Do not generalise this into a second self-reload** — the
 * update rail's is still refused, and D137's first clause is untouched.
 *
 * ## THE BOUND
 *
 * One reload per app process, claimed from a **native** marker (`pwa/recoveryMarker.ts`), released
 * only when a real IndexedDB operation completes. If the reload does not help, the claim is still
 * taken and the second occurrence falls through to the sign-out — so the worst case is one reload,
 * never a loop. **A guard in `localStorage` could not do this**: WebKit serves it from the same
 * Networking process, so it is unreadable exactly when it is consulted.
 *
 * Reported on **both** outcomes. A reload that heals the resident invisibly and reports nothing is
 * the blindness the sink exists to remove, one layer out — and before this, the silent sign-out was
 * the one path in the client that fired no event at all.
 */
export async function recoverLostStoreConnection(
  failure: unknown,
  reload: () => void = () => globalThis.location.reload(),
): Promise<boolean> {
  if (!(failure instanceof StoreConnectionLostError)) return false

  const granted = await claimStoreReload()
  let reloaded = false
  if (granted) {
    try {
      reload()
      reloaded = true
    } catch (error) {
      // A reload that throws is a document that is staying. Say so, so the caller signs out rather
      // than returning to a screen that will never recover.
      console.warn('store-connection-reload-failed', error)
    }
  }
  // **Reported AFTER the attempt, and the outcome is what happened rather than what was
  // authorised.** A `reload()` that throws leaves the resident on the sign-out path, paying a fresh
  // SMS and one of five D65/D88 slots — counting them as `reloaded` corrupts the one denominator
  // this event exists to produce, and the case that proves the sign-out asserts only the return
  // value, so the mislabel was invisible. Still not awaited, and the window is unchanged:
  // `location.reload()` returns synchronously and the document lives until the navigation commits,
  // so the send starts where it always did, and a diagnostic must not delay a teardown the resident
  // is already watching.
  void reportStoreConnectionLost({
    signal: failure.signal,
    outcome: reloaded ? 'reloaded' : 'declined',
  })
  return reloaded
}

/**
 * **A rung has not succeeded if what it rebuilt is a memory store, and this is the guard that
 * keeps the ladder from quietly reversing D128.**
 *
 * `openDeviceStore()` **never rejects** — `openOrDegrade()` catches everything and returns a
 * memory store whose `put` always resolves. So a device whose IndexedDB is broken at the engine
 * level would take rung 1, get a memory store back, have the retried credential write *succeed*
 * against it, and report `rung1`. The resident would be signed in, see no error — and lose the
 * session on their next reload, with `durableStorage: false` computed and rendered by nothing
 * (G26). That is precisely the **degraded memory-store session the operator explicitly declined**,
 * arrived at by accident instead of by decision.
 *
 * So a non-durable store fails its rung. The ladder falls through to the next one, and if none
 * survives it fails closed with the original error, which is the honest dead end D128 asks for.
 */
function durableOrThrow(store: KeyValueStore): KeyValueStore {
  if (!store.durable) throw new Error('recovery-degraded-to-memory')
  return store
}

/** Re-exported from the store since D306, which needed it on the open path too. */
export { errorNameOf }

/** One raw `outbox` row, uninterpreted. */
interface SalvagedRow {
  readonly key: string
  readonly value: unknown
}

export interface CredentialRecovery {
  readonly rung: RecoveryRung
  /**
   * The live store after recovery. **A caller caching a handle must adopt this one** — rung 1
   * destroyed the database the old handle pointed at, and rung 2 replaced the handle itself.
   */
  readonly store: KeyValueStore
}

/**
 * Run the ladder for a rejected credential write, report the outcome, and hand back the store to
 * adopt. **It never throws**: a `rung` of `'none'` is the honest dead end, and the caller fails
 * closed on it (D128 untouched — the degraded memory-store session was explicitly declined).
 */
export async function recoverCredentialWrite(input: {
  readonly failure: unknown
  readonly store: KeyValueStore
  readonly write: (store: KeyValueStore) => Promise<void>
}): Promise<CredentialRecovery> {
  const started = performance.now()
  // The **first** failure's name: the cause, read before any rung has changed the state.
  const errorName = errorNameOf(input.failure)
  let store = input.store
  let rung: RecoveryRung = 'none'
  let salvaged: readonly SalvagedRow[] = []
  let replayOk = true

  if (isStructuralStoreFailure(input.failure)) {
    salvaged = await salvage(store)

    // Rung 1 — destroy and rebuild. Invisible to the resident when it works.
    try {
      await store.destroy()
      store = durableOrThrow(await openDeviceStore())
      await input.write(store)
      rung = 'rung1'
    } catch {
      /* fall through — rung 2 is a different mechanism, not a retry of this one */
    }

    // Rung 2 — a second attempt on a fresh handle, **not a page reload.** It is reachable only
    // because rung 1 failed at one of its three steps (destroy threw / the reopen degraded to
    // memory / the retried write failed); after a *successful* destroy rung 1 has already opened a
    // fresh connection, since `destroy()` clears the cache itself. See `reopenDeviceStore()`.
    // A reload here would cost the
    // resident a fresh OTP: the code they just typed is consumed, so they would land back at phone
    // entry and have to request another SMS — spending one of their five D65/D88 slots per rolling
    // 5 h on our own recovery path. That is a real cost charged to the user for a mechanism whose
    // incremental value is small. `destroy()` has already closed the connection and the container
    // is a single WebView, so the "release a blocked deleteDatabase" argument for a fresh document
    // is spent anyway.
    if (rung === 'none') {
      try {
        store = durableOrThrow(await reopenDeviceStore())
        await input.write(store)
        rung = 'rung2'
      } catch {
        /* the honest dead end */
      }
    }

    // **ONLY NOW.** See the ordering argument in this file's header — the sign-in is committed
    // above, so nothing below can cost it.
    if (rung !== 'none') replayOk = await replay(store, salvaged)
  }

  // Emitted on every failure, including one rung 1 healed silently: a silent recovery that reports
  // nothing is the blindness that cost this incident its evidence. Not awaited — the resident is
  // not kept waiting on a diagnostic, and `reportCredentialStoreFailure` never rejects.
  void reportCredentialStoreFailure({
    errorName,
    rung,
    salvagedReceipts: salvaged.length,
    replayOk,
    elapsedMs: performance.now() - started,
  })

  return { rung, store }
}

/**
 * Best-effort raw copy of `outbox`, **under a budget of its own.**
 *
 * Without one it would inherit {@link STORE_TRANSACTION_TIMEOUT_MS} per row, and a wedged read
 * would burn it in full in front of a resident already watching a spinner after typing a correct
 * code. A false timeout here costs nothing: proceeding without receipts is already this step's
 * semantic.
 *
 * **Raw key → value, with nothing interpreted.** Not `readOutbox()`, for two reasons that both
 * point the same way: it is partitioned by `stage:userId` and the destroy takes *every* partition,
 * including an account this sign-in is not for; and `outbox.ts` states that a row this build
 * cannot parse is **left in place, not repaired and not deleted** — it belongs to whichever
 * version can read it. A salvage that validated rows would quietly drop exactly those.
 */
async function salvage(store: KeyValueStore): Promise<readonly SalvagedRow[]> {
  const budget = deadline(STORE_SALVAGE_TIMEOUT_MS)
  try {
    const rows = await budget.settle(readAllRows(store))
    return rows === TIMED_OUT ? [] : rows
  } catch {
    // Nothing was recoverable. Proceed — this is the step that is allowed to find nothing.
    return []
  } finally {
    budget.cancel()
  }
}

async function readAllRows(store: KeyValueStore): Promise<readonly SalvagedRow[]> {
  const rows: SalvagedRow[] = []
  for (const key of await store.keys('outbox')) {
    rows.push({ key, value: await store.get('outbox', key) })
  }
  return rows
}

/** `true` when every salvaged row is back. Best-effort by design; the sign-in is already safe. */
async function replay(store: KeyValueStore, rows: readonly SalvagedRow[]): Promise<boolean> {
  let ok = true
  for (const row of rows) {
    if (row.value === undefined) continue
    try {
      await store.put('outbox', row.key, row.value)
    } catch {
      // Reported, not thrown: `replayOk: false` in the sink is how a lost receipt becomes visible.
      ok = false
    }
  }
  return ok
}
